import { SyntheticEvent, useMemo } from 'react';

import { ConfirmDialog, LinearProgress, Typography } from '@occc-app/design-system';

export type AudienceActionConfirmDialogProps = {
  open: boolean;
  actionType: 'activate' | 'archive' | 'unarchive';
  loading: boolean;
  audienceName: string;
  onClose(event: SyntheticEvent): void;
  onActivate(): void;
  onArchive(): void;
  onUnarchive(): void;
};

export const AudienceActionConfirmDialog: React.FC<AudienceActionConfirmDialogProps> = (props) => {
  const { open, loading, actionType, audienceName, onClose, onActivate, onArchive, onUnarchive } =
    props;
  const { title, description, confirmButtonText, handler } = useMemo<{
    title: string;
    description: string;
    confirmButtonText: string;
    handler(): void;
  }>(() => {
    if (actionType === 'activate') {
      return {
        title: 'Activate Audience',
        description: `Are you sure you would like to activate "${audienceName}"?`,
        confirmButtonText: 'Activate',
        handler: onActivate,
      };
    }
    if (actionType === 'archive') {
      return {
        title: 'Archive Audience',
        description: `Are you sure you would like to archive "${audienceName}"?`,
        confirmButtonText: 'Archive',
        handler: onArchive,
      };
    }
    if (actionType === 'unarchive') {
      return {
        title: 'Unarchive Audience',
        description:
          'Are you sure you would like to unarchive this audience? This will make it active again and members will begin to enter/exit this audience.',
        confirmButtonText: 'Unarchive',
        handler: onUnarchive,
      };
    }

    return {
      title: '',
      description: '',
      confirmButtonText: '',
      handler: () => undefined,
    };
  }, [actionType, audienceName, onActivate, onArchive, onUnarchive]);

  return (
    <ConfirmDialog
      dialogTitle={title}
      onClose={onClose}
      open={open}
      maxWidth='xs'
      confirmButtonText={confirmButtonText}
      confirmButtonProps={{
        onClick: handler,
        disabled: loading,
        color: 'error',
      }}
    >
      {loading && <LinearProgress />}
      <Typography>{description}</Typography>
    </ConfirmDialog>
  );
};
